import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Link2, 
  Check, 
  Loader2,
  Star,
  Swords,
  ChevronRight,
  ArrowLeft,
  History,
  Trophy,
  Target,
  TrendingUp,
  Globe,
  Calendar,
  Share2
} from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { DIFFICULTIES } from '../../lib/constants';
import { cn, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';
import { API, getChallengeUrl, handleCopyToClipboard } from './challengeUtils';
import { ChallengeShareActions } from './ChallengeShareActions';

// Format date for display (e.g., "Dec 28" or "Dec 28, 2025" if different year)
const formatLeaderboardDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const now = new Date();
  const isCurrentYear = date.getFullYear() === now.getFullYear();
  
  const options = { month: 'short', day: 'numeric' };
  if (!isCurrentYear) {
    options.year = 'numeric';
  }
  return date.toLocaleDateString('en-US', options);
};

export function AvailableChallengesModal({ 
  isOpen, 
  onClose,
  onPlayChallenge,
  deviceId
}) {
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'mine' | 'history'
  const [allChallenges, setAllChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMine, setIsLoadingMine] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  // Tracks which tab the user came from when opening the detail view.
  // When 'mine', the bottom CTA becomes "Share Challenge" instead of
  // "Play This Challenge" and tapping it switches to the share view.
  const [selectedFromTab, setSelectedFromTab] = useState(null);
  const [showShareView, setShowShareView] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // History state
  const [historyData, setHistoryData] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  const loadAllChallenges = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/challenges?limit=40&is_mobile=${isMobileDevice()}`);
      setAllChallenges(response.data.challenges || []);
    } catch (error) {
      logger.error('Failed to load all challenges:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  const loadMyChallenges = useCallback(async () => {
    if (!deviceId) return;
    
    setIsLoadingMine(true);
    try {
      const response = await axios.get(`${API}/challenges?device_id=${deviceId}&limit=40&is_mobile=${isMobileDevice()}`);
      setMyChallenges(response.data.challenges || []);
    } catch (error) {
      logger.error('Failed to load my challenges:', error);
    } finally {
      setIsLoadingMine(false);
    }
  }, [deviceId]);
  
  const loadHistory = useCallback(async () => {
    if (!deviceId) return;
    
    setIsLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/challenges/history/${deviceId}?is_mobile=${isMobileDevice()}`);
      setHistoryData(response.data);
    } catch (error) {
      logger.error('Failed to load history:', error);
      setHistoryData({ total_challenges: 0, challenges: [], head_to_head: [] });
    } finally {
      setIsLoadingHistory(false);
    }
  }, [deviceId]);
  
  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAllChallenges();
      setSelectedChallenge(null);
      setSelectedFromTab(null);
      setShowShareView(false);
      setLeaderboard([]);
      setActiveTab('all');
      setHistoryData(null);
    }
  }, [isOpen, loadAllChallenges]);
  
  // Load my challenges when switching to mine tab
  useEffect(() => {
    if (activeTab === 'mine' && deviceId && myChallenges.length === 0) {
      loadMyChallenges();
    }
  }, [activeTab, deviceId, myChallenges.length, loadMyChallenges]);
  
  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history' && deviceId && !historyData) {
      loadHistory();
    }
  }, [activeTab, deviceId, historyData, loadHistory]);
  
  const loadLeaderboard = async (code) => {
    setIsLoadingLeaderboard(true);
    setSelectedFromTab(activeTab);
    setShowShareView(false);
    try {
      const response = await axios.get(`${API}/challenges/${code}/leaderboard?is_mobile=${isMobileDevice()}`);
      setLeaderboard(response.data.leaderboard || []);
      setSelectedChallenge(response.data);
    } catch (error) {
      logger.error('Failed to load leaderboard:', error);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };
  
  const handleCopy = async (code) => {
    const urlToCopy = getChallengeUrl(code);
    handleCopyToClipboard(
      urlToCopy,
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      (url) => alert(`Copy this URL: ${url}`)
    );
  };
  
  const getResultColor = (result) => {
    if (result === 'won') return 'text-success';
    if (result === 'lost') return 'text-destructive';
    return 'text-warning';
  };
  
  const getResultBg = (result) => {
    if (result === 'won') return 'bg-success/10 border-success/30';
    if (result === 'lost') return 'bg-destructive/10 border-destructive/30';
    return 'bg-warning/10 border-warning/30';
  };
  
  // Get the challenges to display based on active tab
  const displayChallenges = activeTab === 'all' ? allChallenges : myChallenges;
  const isLoadingChallenges = activeTab === 'all' ? isLoading : isLoadingMine;
  
  // Render challenge list item
  const renderChallengeItem = (challenge) => (
    <div 
      key={challenge.challenge_code}
      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted/70 cursor-pointer transition-colors"
      onClick={() => loadLeaderboard(challenge.challenge_code)}
      data-testid={`challenge-item-${challenge.challenge_code}`}
    >
      <img 
        src={challenge.image_url} 
        alt="Challenge" 
        className="w-12 h-12 rounded-lg object-cover"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">{challenge.challenge_code}</p>
          {challenge.creator_name && activeTab === 'all' && (
            <span className="text-xs text-muted-foreground">by {challenge.creator_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <Badge variant="outline" className="text-xs">
            {DIFFICULTIES[challenge.difficulty]?.name}
          </Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3" />
            {challenge.participant_count || 0}
          </span>
        </div>
        {/* Show creator's score if available */}
        {challenge.creator_score && (
          <p className="text-xs text-muted-foreground mt-1">
            Score: <span className="font-medium text-foreground">{Math.round(challenge.creator_score.score)}</span>
            {' · '}
            {formatTime(challenge.creator_score.time_seconds)}
            {' · '}
            {challenge.creator_score.moves} moves
            {challenge.creator_score.perfect_game && (
              <Star className="h-3 w-3 text-warning fill-warning inline ml-1" />
            )}
          </p>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </div>
  );
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[90vh] sm:max-h-[85vh] flex flex-col box-border overflow-y-auto" data-testid="available-challenges-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={
                showShareView
                  ? () => setShowShareView(false)
                  : selectedChallenge
                  ? () => { setSelectedChallenge(null); setSelectedFromTab(null); }
                  : onClose
              }
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Swords className="h-5 w-5 text-primary" />
            Challenges
          </DialogTitle>
        </DialogHeader>
        
        {/* Tabs */}
        {!selectedChallenge && (
          <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
            <button
              onClick={() => setActiveTab('all')}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-colors",
                activeTab === 'all' 
                  ? "bg-background shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="tab-all-challenges"
            >
              <Globe className="h-3.5 w-3.5" />
              All
            </button>
            <button
              onClick={() => setActiveTab('mine')}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-colors",
                activeTab === 'mine' 
                  ? "bg-background shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="tab-my-challenges"
            >
              <Swords className="h-3.5 w-3.5" />
              Mine
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-colors",
                activeTab === 'history' 
                  ? "bg-background shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="tab-history"
            >
              <History className="h-3.5 w-3.5" />
              History
            </button>
          </div>
        )}
        
        <div className="mt-2 overflow-y-auto flex-1">
          {selectedChallenge && showShareView ? (
            // Share Challenge View — mirrors the "Score Saved!" screen,
            // but reads "Share Challenge" and uses the existing challenge.
            <div className="p-2 sm:p-4 space-y-4 sm:space-y-6 relative" data-testid="share-challenge-view">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowShareView(false)}
                className="-ml-2"
                data-testid="share-challenge-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-3 sm:mb-4">
                  <Share2 className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <h2 className="text-xl sm:text-2xl font-heading font-bold">Share Challenge</h2>
                <p className="text-muted-foreground mt-1 text-sm sm:text-base whitespace-pre-line">
                  Share your achievement!{'\n'}and Challenge your Friends
                </p>
              </div>

              {/* Challenge meta (image + code) */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                <img 
                  src={selectedChallenge.image_url} 
                  alt="Challenge puzzle" 
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-mono font-semibold text-base">{selectedChallenge.challenge_code}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {DIFFICULTIES[selectedChallenge.difficulty]?.name}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {leaderboard.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Same View / Facebook / Other Social Media triplet as Score Saved */}
              <ChallengeShareActions
                imageUrl={selectedChallenge.image_url}
                difficulty={selectedChallenge.difficulty}
                deviceId={deviceId}
                playerName={selectedChallenge.creator_name || ''}
                existingChallengeCode={selectedChallenge.challenge_code}
              />
            </div>
          ) : selectedChallenge ? (
            // Challenge Leaderboard View
            <div className="space-y-4" data-testid="challenge-leaderboard-view">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setSelectedChallenge(null);
                  setSelectedFromTab(null);
                  setShowShareView(false);
                  setLeaderboard([]);
                }}
                className="-ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              
              {/* Challenge Info Header */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <img 
                  src={selectedChallenge.image_url} 
                  alt="Challenge puzzle" 
                  className="w-14 h-14 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">{selectedChallenge.challenge_code}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {DIFFICULTIES[selectedChallenge.difficulty]?.name}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {leaderboard.length}
                    </span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCopy(selectedChallenge.challenge_code)}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
              
              {/* Leaderboard */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
                  Leaderboard
                </p>
                
                {isLoadingLeaderboard ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No players yet</p>
                    <p className="text-xs mt-1">Share the link to get competitors!</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1" data-testid="leaderboard-list">
                    {leaderboard.map((entry, index) => (
                      <div 
                        key={entry.id}
                        className={cn(
                          "flex items-center gap-2 p-2.5 rounded-lg text-sm",
                          entry.player_name === selectedChallenge.creator_name 
                            ? "bg-accent/10 border border-accent/30"
                            : "bg-muted/30"
                        )}
                      >
                        <span className={cn(
                          "w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold",
                          index === 0 && "bg-yellow-500 text-white",
                          index === 1 && "bg-gray-400 text-white",
                          index === 2 && "bg-amber-600 text-white",
                          index > 2 && "bg-muted text-muted-foreground"
                        )}>
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium truncate">{entry.player_name}</span>
                            {entry.player_name === selectedChallenge.creator_name && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/20 text-accent-foreground font-medium">HOST</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatTime(entry.time_seconds)} · {entry.moves} moves</span>
                            {entry.created_at && (
                              <span className="flex items-center gap-0.5">
                                <Calendar className="h-2.5 w-2.5" />
                                {formatLeaderboardDate(entry.created_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{Math.round(entry.score)}</p>
                          {entry.perfect_game && <Star className="h-3 w-3 text-warning fill-warning inline" />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Play this challenge OR Share Challenge (when from "Mine" tab) */}
              {selectedFromTab === 'mine' ? (
                <Button 
                  onClick={() => setShowShareView(true)}
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  data-testid="share-this-challenge-btn"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Challenge
                </Button>
              ) : (
                <Button 
                  onClick={() => {
                    onPlayChallenge?.(selectedChallenge.image_url, selectedChallenge.difficulty, selectedChallenge.challenge_code);
                    onClose();
                  }}
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  data-testid="play-this-challenge-btn"
                >
                  <Swords className="h-4 w-4 mr-2" />
                  Play This Challenge
                </Button>
              )}
            </div>
          ) : activeTab === 'history' ? (
            // History Tab
            <div className="space-y-4" data-testid="history-tab">
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : !historyData || historyData.total_challenges === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No challenge history</p>
                  <p className="text-xs mt-1">Play challenges to build your record!</p>
                </div>
              ) : (
                <>
                  {/* Overall Stats */}
                  <div className="grid grid-cols-2 gap-2" data-testid="history-stats">
                    <div className="p-3 rounded-lg bg-muted/50 text-center">
                      <Trophy className="h-5 w-5 mx-auto mb-1 text-success" />
                      <p className="text-xl font-bold">{historyData.wins}</p>
                      <p className="text-xs text-muted-foreground">Wins</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50 text-center">
                      <Target className="h-5 w-5 mx-auto mb-1 text-destructive" />
                      <p className="text-xl font-bold">{historyData.losses}</p>
                      <p className="text-xs text-muted-foreground">Losses</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50 text-center">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1 text-primary" />
                      <p className="text-xl font-bold">{historyData.win_rate}%</p>
                      <p className="text-xs text-muted-foreground">Win Rate</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50 text-center">
                      <Star className="h-5 w-5 mx-auto mb-1 text-warning" />
                      <p className="text-xl font-bold">{historyData.average_score}</p>
                      <p className="text-xs text-muted-foreground">Avg Score</p>
                    </div>
                  </div>
                  
                  {/* Additional Stats Row */}
                  <div className="flex justify-center gap-4 text-sm">
                    <div className="text-center">
                      <span className="font-bold">{historyData.total_challenges}</span>
                      <span className="text-muted-foreground ml-1">Played</span>
                    </div>
                    <div className="text-center">
                      <span className="font-bold">{historyData.ties}</span>
                      <span className="text-muted-foreground ml-1">Ties</span>
                    </div>
                    <div className="text-center">
                      <span className="font-bold">{historyData.perfect_games}</span>
                      <span className="text-muted-foreground ml-1">Perfect</span>
                    </div>
                  </div>
                  
                  {/* Head-to-Head Records */}
                  {historyData.head_to_head && historyData.head_to_head.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
                        Head-to-Head Records
                      </p>
                      <div className="space-y-1.5" data-testid="head-to-head-list">
                        {historyData.head_to_head.map((opponent) => (
                          <div 
                            key={opponent.player_name}
                            className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30"
                          >
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{opponent.player_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {opponent.challenges_together} challenges
                              </p>
                            </div>
                            <div className="text-right text-sm">
                              <span className="text-success font-bold">{opponent.wins}W</span>
                              <span className="text-muted-foreground mx-1">-</span>
                              <span className="text-destructive font-bold">{opponent.losses}L</span>
                              {opponent.ties > 0 && (
                                <>
                                  <span className="text-muted-foreground mx-1">-</span>
                                  <span className="text-warning font-bold">{opponent.ties}T</span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Recent Challenges */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
                      Recent Challenges
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1" data-testid="recent-challenges-list">
                      {historyData.challenges.slice(0, 10).map((challenge) => (
                        <div 
                          key={challenge.challenge_code}
                          className={cn(
                            "flex items-center gap-3 p-2.5 rounded-lg border",
                            getResultBg(challenge.result)
                          )}
                        >
                          <img 
                            src={challenge.image_url} 
                            alt="Challenge" 
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{challenge.challenge_code}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                {DIFFICULTIES[challenge.difficulty]?.name}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              #{challenge.my_rank} of {challenge.total_participants} · {Math.round(challenge.my_score)} pts
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={cn("text-sm font-bold uppercase", getResultColor(challenge.result))}>
                              {challenge.result}
                            </p>
                            {challenge.perfect_game && (
                              <Star className="h-3 w-3 text-warning fill-warning inline" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            // All Challenges or My Challenges Tab
            <div className="space-y-3">
              {isLoadingChallenges ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : displayChallenges.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {activeTab === 'all' ? (
                    <>
                      <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No challenges yet</p>
                      <p className="text-xs mt-1">Be the first to create a challenge!</p>
                    </>
                  ) : (
                    <>
                      <Swords className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No challenges created</p>
                      <p className="text-xs mt-1">Complete a puzzle and create a challenge!</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-2" data-testid="challenges-list">
                  {displayChallenges.map(renderChallengeItem)}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
